import { useState } from 'react'
import { RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import Modal from '@/components/ui/modal'
import { useRedeliverWebhook, useWebhookDeliveries } from '@/hooks/useOrderWebhooks'
import type {
  WebhookDeliveryStatus,
  WebhookEndpoint,
} from '@/services/orderWebhooksApi'

const STATUS_FILTERS: Array<{ value?: WebhookDeliveryStatus; label: string }> = [
  { value: undefined, label: 'Todos' },
  { value: 'delivered', label: 'Entregados' },
  { value: 'retrying', label: 'Reintentando' },
  { value: 'failed', label: 'Fallidos' },
  { value: 'pending', label: 'Pendientes' },
]

const STATUS_LABEL: Record<WebhookDeliveryStatus, string> = {
  pending: 'Pendiente',
  retrying: 'Reintentando',
  delivered: 'Entregado',
  failed: 'Fallido',
}

const STATUS_CLASS: Record<WebhookDeliveryStatus, string> = {
  pending: 'bg-slate-100 text-slate-700',
  retrying: 'bg-amber-100 text-amber-800',
  delivered: 'bg-emerald-100 text-emerald-800',
  failed: 'bg-red-100 text-red-800',
}

interface WebhookDeliveriesModalProps {
  endpoint: WebhookEndpoint
  onClose: () => void
}

function formatDate(value: string): string {
  return new Date(value).toLocaleString('es-VE', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function WebhookDeliveriesModal({ endpoint, onClose }: WebhookDeliveriesModalProps) {
  const [status, setStatus] = useState<WebhookDeliveryStatus | undefined>(undefined)
  const [redeliveringId, setRedeliveringId] = useState<string | null>(null)
  const deliveriesQuery = useWebhookDeliveries(endpoint.id, status)
  const redeliverMutation = useRedeliverWebhook()

  async function redeliver(id: string) {
    setRedeliveringId(id)
    try {
      await redeliverMutation.mutateAsync(id)
    } catch {
      // El hook ya muestra el mensaje accionable.
    } finally {
      setRedeliveringId(null)
    }
  }

  return (
    <Modal isOpen onClose={onClose} title="Historial de avisos" size="xl">
      <div className="space-y-4">
        <p className="break-all text-sm text-gray-600">{endpoint.description || endpoint.url}</p>

        <div className="flex flex-wrap gap-2">
          {STATUS_FILTERS.map((filter) => {
            const active = status === filter.value
            return (
              <button
                key={filter.label}
                type="button"
                onClick={() => setStatus(filter.value)}
                aria-pressed={active}
                className={`rounded-full border px-3 py-1 text-xs font-medium ${
                  active
                    ? 'border-[#075E54] bg-[#075E54] text-white'
                    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                {filter.label}
              </button>
            )
          })}
        </div>

        {deliveriesQuery.isLoading && (
          <div className="space-y-2" aria-label="Cargando historial">
            {[0, 1, 2].map((row) => (
              <div key={row} className="h-10 animate-pulse rounded-md bg-gray-100" />
            ))}
          </div>
        )}

        {deliveriesQuery.isError && (
          <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">
            <p>No se pudo cargar el historial.</p>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="mt-2"
              onClick={() => void deliveriesQuery.refetch()}
            >
              Reintentar
            </Button>
          </div>
        )}

        {!deliveriesQuery.isLoading && !deliveriesQuery.isError && deliveriesQuery.data?.data.length === 0 && (
          <p className="rounded-lg bg-gray-50 p-4 text-sm text-gray-600">
            Todavía no hay envíos para este destino. Cuando ocurra un pedido con los avisos elegidos aparecerá aquí.
          </p>
        )}

        {!deliveriesQuery.isLoading && !deliveriesQuery.isError && (deliveriesQuery.data?.data.length ?? 0) > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead className="border-b border-gray-200 text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-2 py-2">Fecha</th>
                  <th className="px-2 py-2">Aviso</th>
                  <th className="px-2 py-2">Estado</th>
                  <th className="px-2 py-2">Código</th>
                  <th className="px-2 py-2">Intentos</th>
                  <th className="px-2 py-2 text-right">Acción</th>
                </tr>
              </thead>
              <tbody>
                {deliveriesQuery.data?.data.map((delivery) => (
                  <tr key={delivery.id} className="border-b border-gray-100 align-top">
                    <td className="px-2 py-2 text-gray-600">{formatDate(delivery.createdAt)}</td>
                    <td className="px-2 py-2 text-gray-800">{delivery.eventType ?? '—'}</td>
                    <td className="px-2 py-2">
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CLASS[delivery.status]}`}
                      >
                        {STATUS_LABEL[delivery.status]}
                      </span>
                      {delivery.lastError && (
                        <details className="mt-1 max-w-[220px] text-xs text-gray-500">
                          <summary className="cursor-pointer">Ver detalle</summary>
                          <p className="mt-1 whitespace-pre-wrap break-words">{delivery.lastError}</p>
                          {delivery.lastResponseSnippet && (
                            <pre className="mt-1 max-h-24 overflow-auto whitespace-pre-wrap break-words rounded bg-gray-50 p-1 text-[11px]">
                              {delivery.lastResponseSnippet}
                            </pre>
                          )}
                        </details>
                      )}
                    </td>
                    <td className="px-2 py-2 text-gray-600">{delivery.lastStatusCode ?? '—'}</td>
                    <td className="px-2 py-2 text-gray-600">{delivery.attempts}</td>
                    <td className="px-2 py-2 text-right">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={redeliveringId === delivery.id}
                        onClick={() => void redeliver(delivery.id)}
                      >
                        <RefreshCw className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
                        {redeliveringId === delivery.id ? 'Programando…' : 'Reenviar'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="text-xs text-gray-500">
          Conservamos el historial 30 días. Los envíos fallidos se reintentan solos hasta 8 veces.
        </p>
      </div>
    </Modal>
  )
}
